import { Link } from "wouter";
import { NavPublic } from "@/components/NavPublic";
import { CheckCircle, Upload, Settings, Rocket, FileText, Bell, BarChart3, Building2, Shield, Lock } from "lucide-react";

export function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <NavPublic />

      {/* Hero Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          {/* Headline */}
          <h1 className="page-title text-4xl sm:text-5xl font-bold text-black mb-8 leading-tight tracking-tight">
            Smarter Audits. Safer Care.
          </h1>
          
          {/* Subheadline */}
          <p className="text-lg sm:text-xl text-gray-600 font-normal mb-12 max-w-3xl mx-auto leading-relaxed">
            Tailored for UK Care Homes. Import existing audits or build new ones with our no-code template builder.
          </p>
          
          {/* Feature Bullets - Horizontal */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 mb-12 text-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Upload className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-base font-medium">Import your own templates</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-base font-medium">Live preview and scoring</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Bell className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-base font-medium">Due/overdue notifications</span>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link href="/demo" className="inline-block px-8 py-4 bg-[hsl(var(--brand-primary))] text-white font-semibold rounded-lg hover:bg-[hsl(var(--secondary))] transition-colors shadow-lg shadow-purple-500/30" data-testid="button-book-demo">
              Book a Demo
            </Link>
            <a href="#templates" className="inline-block px-8 py-4 border-2 border-[hsl(var(--brand-primary))] text-[hsl(var(--brand-primary))] font-semibold rounded-lg hover:bg-purple-50 transition-colors">
              See Template Library
            </a>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="solutions" className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">How it works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <Upload className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">1. Import</h3>
              <p className="text-gray-600">Upload your existing audit templates during onboarding. We handle the migration.</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <Settings className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">2. Configure</h3>
              <p className="text-gray-600">Customize templates with our no-code builder. Set scoring, notifications, and permissions.</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <Rocket className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">3. Go live</h3>
              <p className="text-gray-600">Your team can start completing audits immediately. Track compliance in real-time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Everything you need</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="p-6 border border-gray-200 rounded-lg">
              <FileText className="w-10 h-10 text-purple-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Template builder</h3>
              <p className="text-gray-600">No-code builder for creating and editing audit templates to match your exact requirements.</p>
            </div>
            <div className="p-6 border border-gray-200 rounded-lg">
              <Bell className="w-10 h-10 text-purple-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Notifications</h3>
              <p className="text-gray-600">Automated alerts for due and overdue audits to keep your team on track.</p>
            </div>
            <div className="p-6 border border-gray-200 rounded-lg">
              <BarChart3 className="w-10 h-10 text-purple-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Reporting (PDF/CSV)</h3>
              <p className="text-gray-600">Export comprehensive reports for inspections and internal reviews.</p>
            </div>
            <div className="p-6 border border-gray-200 rounded-lg">
              <Building2 className="w-10 h-10 text-purple-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Multi-home oversight</h3>
              <p className="text-gray-600">Manage compliance across all your care homes from a single dashboard.</p>
            </div>
            <div className="p-6 border border-gray-200 rounded-lg">
              <Shield className="w-10 h-10 text-purple-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Role-based access</h3>
              <p className="text-gray-600">Granular permissions ensure staff only see what they need to see.</p>
            </div>
            <div className="p-6 border border-gray-200 rounded-lg">
              <Lock className="w-10 h-10 text-purple-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">UK-hosted security</h3>
              <p className="text-gray-600">Your data stays in the UK with enterprise-grade security and compliance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Template Library */}
      <section id="templates" className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">Template Library</h2>
          <p className="text-center text-gray-600 mb-12">Example audit types we support</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            <div className="p-4 bg-white rounded-lg border border-gray-200">Clinical Care Audits</div>
            <div className="p-4 bg-white rounded-lg border border-gray-200">Health & Safety Audits</div>
            <div className="p-4 bg-white rounded-lg border border-gray-200">Medication Management</div>
            <div className="p-4 bg-white rounded-lg border border-gray-200">Infection Control</div>
            <div className="p-4 bg-white rounded-lg border border-gray-200">Environmental Audits</div>
            <div className="p-4 bg-white rounded-lg border border-gray-200">Staff Competency Checks</div>
            <div className="p-4 bg-white rounded-lg border border-gray-200">Dignity & Respect Audits</div>
            <div className="p-4 bg-white rounded-lg border border-gray-200">Nutrition & Hydration</div>
            <div className="p-4 bg-white rounded-lg border border-gray-200">Care Planning Reviews</div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Simple, per-home pricing</h2>
          <p className="text-xl text-gray-600 mb-8">Always more affordable than legacy systems</p>
          <div className="bg-gray-50 rounded-lg p-8 max-w-2xl mx-auto">
            <ul className="space-y-3 text-left">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>Unlimited users per care home</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>Full template builder and audit tools</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>PDF and CSV export for all reports</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>Multi-home management dashboard</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>Email and in-app notifications</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <span>Priority support and onboarding</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Onboarding & Migration */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-purple-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Onboarding & migration included</h2>
          <p className="text-lg text-gray-700 mb-6">
            We migrate your audits, users, and homes at onboarding. Our team handles the entire setup process so you can focus on running your care homes.
          </p>
          <p className="text-gray-600 mb-8">
            Book a demo to get a tailored quote based on your number of homes and specific requirements.
          </p>
          <Link href="/demo" className="inline-block px-8 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors">
            Book Your Demo
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer id="about" className="bg-gray-900 text-gray-300 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="text-white font-semibold mb-4">Smart Care Audits</h3>
              <p className="text-sm">
                Modern audit and compliance management for UK care homes.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="/privacy" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white">Terms of Service</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:info@smartcareaudits.co.uk" className="hover:text-white">info@smartcareaudits.co.uk</a></li>
                <li><Link href="/demo" className="hover:text-white">Book a Demo</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-sm text-center">
            <p>&copy; {new Date().getFullYear()} Smart Care Audits. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
